/*
 * @descrption
 * Questa classe si occupa della gestione della comunicazione con il server
 * per operazioni legate all'autenticazione e alla gestione delle sessioni utente.
 * Estende `AbstractCommunicationController` per fornire funzionalità specifiche
 * come il login e la gestione dei token di sessione.
 * */

import config from "../../config";
import User, { UserData, UserType } from "../models/User";
import AbstractCommunicationController from "./AbstractCommunicationController";
import { LoginResponse } from "../models/AuthTypes";
import { logger } from "../utils/logger";

/**
 * Classe per gestire la comunicazione API relativa all'autenticazione dell'utente.
 *
 * Estende `AbstractCommunicationController` e implementa funzionalità come:
 * - Login utente e gestione della sessione
 * - Configurazione automatica degli header HTTP per autenticazione
 */
class AuthCommunicationController extends AbstractCommunicationController {
    private endpoints = {
        LOGIN: "/user/login",
        LOGOUT: "/user/logout",
        CHANGE_PASSWORD: "/user/:userId/password"
    };

    /**
     * Restituisce gli header personalizzati per ogni richiesta.
     * Se la richiesta non è un login, tenta di recuperare il token di sessione da `localStorage`.
     * Questo assicura che le richieste siano autenticare con il token corretto.
     */
    protected getHeaders(endpoint: string): { [key: string]: string } {
        const isLoginRequest = endpoint === this.endpoints.LOGIN;
        const isReload = localStorage.getItem("reload") === "true";

        if (!isLoginRequest || isReload) return this.getSessionHeader();

        return { [this.SESSION_HEADER_NAME]: "" };
    }

    //Aggiorna il `localStorage` con la sessione dell'utente autenticato.
    private updateSession(res: LoginResponse) {
        const sessionSid = res.session?.sid;
        const sessionExpire = res.session?.expire || "";

        if (!sessionSid) {
            logger.error("Nessun sid ricevuto dal server!");
            return;
        }

        // Leggo le sessioni salvate
        const sessionObjsString = localStorage.getItem(config.LOCAL_STORAGE_SESSION_KEY);
        let sessionObjs: {
            sid: string;
            active: boolean;
            id: number;
            type: string;
            email: string;
            name: string;
            surname: string;
        }[] = sessionObjsString ? JSON.parse(sessionObjsString) : [];

        // Disattiva tutte le sessioni precedenti
        sessionObjs = sessionObjs.map(obj => ({ ...obj, active: false }));

        if (!sessionObjs.some(obj => obj.sid === sessionSid)) {
            sessionObjs.push({
                sid: sessionSid,
                active: true,
                id: res.id,
                type: res.type,
                email: res.email,
                name: res.name,
                surname: res.surname ?? ""
            });
        }

        logger.log("Salvataggio sessione nel localStorage:", sessionObjs);
        localStorage.setItem(config.LOCAL_STORAGE_SESSION_KEY, JSON.stringify(sessionObjs));
        localStorage.setItem(config.LOCAL_STORAGE_EXPIRE_KEY, sessionExpire);
    }

    /**
     * Effettua il login dell'utente e salva la sessione in `localStorage`.
     *
     * - Invia una richiesta POST all'endpoint di login con email e password.
     * - Se il login ha successo, salva il token di sessione e l'utente in locale.
     * - Restituisce un oggetto `User` con le informazioni dell'utente autenticato
     */
    login = async (email?: string, password?: string): Promise<User> => {
        if (email === "") localStorage.setItem("reload", "true");
        else localStorage.setItem("reload", "false");

        //effettua la chiamata API per il login
        const res = await this.post<LoginResponse>(this.endpoints.LOGIN, { email, password, app: "cadet" });
        logger.log(res);

        this.updateSession(res);

        //verifico userType
        const userType = Object.values(UserType).includes(res.type as UserType)
            ? (res.type as UserType)
            : UserType.OPERATOR;

        //Restituisce un oggetto "User"
        return new User(
            { id: res.id, email: res.email, name: res.name, surname: res.surname ?? "", type: userType },
            res.password
        );
    };

    //Effettua il logout dell'utente e rimuove la sessione
    logout = async (): Promise<void> => {
        try {
            await this.post(this.endpoints.LOGOUT);
        } catch (err) {
            logger.error("Errore durante il logout", err);
        } finally {
            localStorage.removeItem(config.LOCAL_STORAGE_SESSION_KEY);
            localStorage.removeItem(config.LOCAL_STORAGE_EXPIRE_KEY);
        }
    };

    /**
     * Cambia la password dell'utente autenticato, verificando lato server la password attuale.
     * Il server risponde 200 in caso di successo o 401 se `oldPassword` non è corretta.
     */
    changePassword = async (userId: number, oldPassword: string, newPassword: string): Promise<void> => {
        await this.patch(this.formatEndpoint(this.endpoints.CHANGE_PASSWORD, { userId }), {
            old_password: oldPassword,
            new_password: newPassword
        });
    };

    /**
     * Resetta la password di un utente (azione riservata agli admin): il server ne genera
     * una nuova casualmente e la restituisce nella response, senza che il client ne proponga una.
     */
    resetPassword = async (userId: number): Promise<string> => {
        const res = await this.patch<UserData & { password: string }>(
            this.formatEndpoint(this.endpoints.CHANGE_PASSWORD, { userId })
        );
        return res.password;
    };

    /**
     * Effettua il login SSO usando solo il sessionId (header ApolloSession).
     * Sezione client‐side, ma passa comunque per lo stesso endpoint di login.
     */
    loginWithSessionId = async (sessionId: string): Promise<User> => {
        logger.log("Tentativo di login con sessionId:", sessionId);

        // Crea una sessione temporanea
        const tempSession = [
            {
                sid: sessionId,
                active: true, // Imposta come attiva
                id: -1,
                type: "",
                email: "",
                name: "",
                surname: ""
            }
        ];

        localStorage.setItem(config.LOCAL_STORAGE_SESSION_KEY, JSON.stringify(tempSession));
        localStorage.setItem("reload", "true");

        try {
            logger.log("Effettuo chiamata a /user/login");

            // Effettua la richiesta con l'header esplicito
            const res = await this.post<LoginResponse>(
                this.endpoints.LOGIN,
                {},
                {
                    [this.SESSION_HEADER_NAME]: sessionId
                }
            );

            if (!res?.id) {
                throw new Error("Risposta del server incompleta");
            }

            // Crea la sessione completa con i dati ricevuti
            const updatedSession = {
                sid: res.session.sid,
                active: true, // IMPORTANTE: mantieni active true
                id: res.id,
                type: res.type,
                email: res.email,
                name: res.name,
                surname: res.surname,
                expire: res.session.expire // aggiungi expire
            };

            // Sostituisci la sessione temporanea con quella completa
            localStorage.setItem(config.LOCAL_STORAGE_SESSION_KEY, JSON.stringify([updatedSession]));
            logger.log("Salvataggio sessione nel localStorage:", [updatedSession]);

            logger.log("Sessione aggiornata dopo login SSO");
            logger.log("Dati utente ricevuti dal server:", res);

            const userType = Object.values(UserType).includes(res.type as UserType)
                ? (res.type as UserType)
                : UserType.OPERATOR;

            return new User(
                {
                    id: res.id,
                    email: res.email,
                    name: res.name,
                    surname: res.surname ?? "",
                    type: userType
                },
                ""
            );
        } catch (error) {
            logger.error("Errore nel login SSO:", error);
            // Pulizia in caso di errore
            localStorage.removeItem(config.LOCAL_STORAGE_SESSION_KEY);
            localStorage.removeItem("reload");
            throw error;
        } finally {
            localStorage.setItem("reload", "false");
        }
    };
}

const instance = new AuthCommunicationController(config.DEANONYMIZATION_API_URL);

export default instance;
